import React, { useState } from "react";
import {
  Card,
  Button,
  // Form,
  // Row,
  // Col,
  Table,
  Badge,
  Modal,
} from "react-bootstrap";
import { patchRequest } from "services/api";
import LayoutAgentRank from "layouts/layout-agent-rank";
import {
  IconRefresh,
  IconSettings,
  IconPlus,
  IconEdit,
  IconTrash,
  IconPlayerStop,
} from "@tabler/icons-react";
import { useTier } from "./context/TierContext";
import TierForm from "./components/TierForm";
import { useSeason } from "./context/SeasonContext";
import SeasonForm from "./components/SeasonForm";
import Swal from "sweetalert2";
import { useDeleteData } from "hooks/useApiHooks";
import PrintJson from "utils/printJson";
import { getContrastColor } from "utils/utils";

export default function AgentRankSetting() {
  const {
    tiers,
    refetch: refetchTiers,
    Pagination: TierPagination,
  } = useTier();
  const { seasons, refetch: refetchSeasons } = useSeason();

  const [showTierModal, setShowTierModal] = useState(false);
  const [showSeasonModal, setShowSeasonModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const { deleteData: deleteTier } = useDeleteData("/tier", "tiers");
  const { deleteData: deleteSeason } = useDeleteData("/season", "seasons");

  const handleCreateTier = () => {
    setSelectedTier(null);
    setShowTierModal(true);
  };

  const handleCreateSeason = () => {
    setSelectedSeason(null);
    setShowSeasonModal(true);
  };

  const handleEditTier = (tier) => {
    setSelectedTier(tier);
    setShowTierModal(true);
  };

  const handleEditSeason = (season) => {
    setSelectedSeason(season);
    setShowSeasonModal(true);
  };

  const handleDeleteTier = async (tier) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete the "${tier.name}" tier?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await deleteTier(tier.id);
        Swal.fire("Deleted!", "Tier has been deleted.", "success");
        refetchTiers();
      } catch (error) {
        Swal.fire("Error!", "Failed to delete tier.", "error");
      }
    }
  };

  const handleDeleteSeason = async (season) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete the "${season.name}" season?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await deleteSeason(season.id);
        Swal.fire("Deleted!", "Season has been deleted.", "success");
        refetchSeasons();
      } catch (error) {
        Swal.fire("Error!", "Failed to delete season.", "error");
      }
    }
  };

  const handleForceEndSeason = async (season) => {
    const result = await Swal.fire({
      title: "Force End Season?",
      text: `Are you sure you want to force end the "${season.name}" season? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, force end it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await patchRequest(`/season/${season.id}/end`);
        if (response.success) {
          Swal.fire("Success!", "Season has been force ended.", "success");
          refetchSeasons();
        } else {
          Swal.fire(
            "Error!",
            response.message || "Failed to force end season.",
            "error"
          );
        }
      } catch (error) {
        console.error("Error force ending season:", error);
        Swal.fire("Error!", "Failed to force end season.", "error");
      }
    }
  };

  const handleTierModalClose = () => {
    setShowTierModal(false);
    setSelectedTier(null);
  };

  const handleSeasonModalClose = () => {
    setShowSeasonModal(false);
    setSelectedSeason(null);
  };

  const handleTierFormSuccess = () => {
    setShowTierModal(false);
    setSelectedTier(null);
    refetchTiers();
    Swal.fire("Success!", "Tier has been saved.", "success");
  };

  const handleSeasonFormSuccess = () => {
    setShowSeasonModal(false);
    setSelectedSeason(null);
    refetchSeasons();
    Swal.fire("Success!", "Season has been saved.", "success");
  };

  const handleTierFormError = (error) => {
    Swal.fire("Error!", error.message || "Failed to save tier.", "error");
  };

  const handleSeasonFormError = (error) => {
    Swal.fire("Error!", error.message || "Failed to save season.", "error");
  };

  const handleImageClick = (imageUrl, tierName) => {
    setSelectedImage({ url: imageUrl, name: tierName });
    setShowImageModal(true);
  };

  return (
    <LayoutAgentRank>
      <div className="container-fluid mt-5">
        <h1>AGENT RANKING - SETTINGS</h1>

        <div
          style={{
            minHeight: "100vh",
            backgroundColor: "#f9fafb",
            padding: "16px",
          }}
        >
          <div>
            {/* Settings Configuration */}
            <div style={{ marginBottom: "32px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                }}
              >
                <h2
                  style={{
                    fontSize: "20px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <IconSettings size={24} />
                  Rank Configuration
                </h2>
                <div style={{ display: "flex", gap: "8px" }}>
                  <Button
                    variant="outline-info"
                    onClick={refetchTiers}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <IconRefresh size={16} />
                    Refresh Data
                  </Button>
                  <Button
                    variant="success"
                    onClick={handleCreateTier}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <IconPlus size={16} />
                    Create Tier
                  </Button>
                </div>
              </div>
            </div>

            {/* Current Rank Configuration Overview */}
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <h2
                  style={{
                    fontSize: "20px",
                    fontWeight: "600",
                    margin: 0,
                  }}
                >
                  Current Rank Configuration
                </h2>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  Total Tiers: {tiers?.data?.length || 0}
                </div>
              </div>

              {tiers?.error && (
                <div className="alert alert-danger" role="alert">
                  <strong>Error loading tiers:</strong> {tiers.error}
                </div>
              )}

              <Card>
                <div style={{ padding: "0" }}>
                  <Table striped bordered hover style={{ marginBottom: "0" }}>
                    <thead>
                      <tr>
                        <th>🎯 Rank</th>
                        <th style={{ textAlign: "center" }}>�️ Image</th>
                        <th style={{ textAlign: "center" }}>
                          �📊 Min Bookings
                        </th>
                        <th style={{ textAlign: "center" }}>📈 Max Bookings</th>
                        <th style={{ textAlign: "center" }}>🎨 Color</th>
                        <th>📅 Created</th>
                        <th style={{ textAlign: "center" }}>⚡ Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tiers?.loading ? (
                        <tr>
                          <td
                            colSpan="7"
                            style={{ textAlign: "center", padding: "20px" }}
                          >
                            <div className="d-flex justify-content-center align-items-center">
                              <div
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                              Loading tiers data...
                            </div>
                          </td>
                        </tr>
                      ) : tiers?.data?.length > 0 ? (
                        tiers.data.map((tier, index) => (
                          <tr key={tier.id || index}>
                            <td style={{ fontWeight: "500" }}>
                              <Badge
                                style={{
                                  backgroundColor: tier.color,
                                  color: getContrastColor(tier.color),
                                }}
                                bg={tier.color}
                                text={getContrastColor(tier.color)}
                              >
                                {tier.name}
                                <br />
                              </Badge>
                            </td>
                            <td style={{ textAlign: "center" }}>
                              {tier.image_url ? (
                                <img
                                  src={tier.image_url}
                                  alt={tier.name}
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    objectFit: "cover",
                                    borderRadius: "4px",
                                    border: "1px solid #e5e7eb",
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    handleImageClick(tier.image_url, tier.name)
                                  }
                                />
                              ) : (
                                <div
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    backgroundColor: "#f3f4f6",
                                    borderRadius: "4px",
                                    border: "1px solid #e5e7eb",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "12px",
                                    color: "#6b7280",
                                  }}
                                >
                                  N/A
                                </div>
                              )}
                            </td>
                            <td
                              style={{ textAlign: "center", fontWeight: "500" }}
                            >
                              {tier.start.toLocaleString()}
                            </td>
                            <td
                              style={{ textAlign: "center", fontWeight: "500" }}
                            >
                              {tier.end === 999999
                                ? "∞"
                                : tier.end.toLocaleString()}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <div
                                style={{
                                  width: "24px",
                                  height: "24px",
                                  backgroundColor: tier.color,
                                  borderRadius: "4px",
                                  margin: "0 auto",
                                  border: "1px solid #e5e7eb",
                                }}
                              />
                            </td>
                            <td>
                              {new Date(tier.created_at).toLocaleDateString(
                                "id-ID"
                              )}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <div
                                style={{
                                  display: "flex",
                                  gap: "4px",
                                  justifyContent: "center",
                                }}
                              >
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleEditTier(tier)}
                                  style={{
                                    padding: "4px 8px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <IconEdit size={14} />
                                  Edit
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeleteTier(tier)}
                                  style={{
                                    padding: "4px 8px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <IconTrash size={14} />
                                  Delete
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="7"
                            style={{ textAlign: "center", padding: "20px" }}
                          >
                            No tier data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
                {TierPagination && (
                  <div style={{ padding: "16px" }}>{TierPagination}</div>
                )}
              </Card>
            </div>

            {/* Season Management */}
            <div style={{ marginTop: "32px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <h2
                  style={{
                    fontSize: "20px",
                    fontWeight: "600",
                    margin: 0,
                  }}
                >
                  Season Management
                </h2>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  Total Seasons: {seasons?.data?.length || 0}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  marginBottom: "16px",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="outline-info"
                  onClick={refetchSeasons}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <IconRefresh size={16} />
                  Refresh
                </Button>
                <Button
                  variant="success"
                  onClick={handleCreateSeason}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <IconPlus size={16} />
                  Create Season
                </Button>
              </div>

              {seasons?.error && (
                <div className="alert alert-danger" role="alert">
                  <strong>Error loading seasons:</strong> {seasons.error}
                </div>
              )}

              <Card>
                <div style={{ padding: "0" }}>
                  <Table striped bordered hover style={{ marginBottom: "0" }}>
                    <thead>
                      <tr>
                        <th>🏷️ Name</th>
                        <th style={{ textAlign: "center" }}>📅 Start Date</th>
                        <th style={{ textAlign: "center" }}>📅 End Date</th>
                        <th>Is Active</th>
                        <th style={{ textAlign: "center" }}>⚡ Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {seasons?.loading ? (
                        <tr>
                          <td
                            colSpan="5"
                            style={{ textAlign: "center", padding: "20px" }}
                          >
                            <div className="d-flex justify-content-center align-items-center">
                              <div
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                              Loading seasons data...
                            </div>
                          </td>
                        </tr>
                      ) : seasons?.data?.length > 0 ? (
                        seasons.data.map((season, index) => (
                          <tr key={season.id || index}>
                            <td style={{ fontWeight: "500" }}>{season.name}</td>
                            <td
                              style={{ textAlign: "center", fontWeight: "500" }}
                            >
                              {new Date(season.start_date).toLocaleDateString(
                                "id-ID"
                              )}
                            </td>
                            <td
                              style={{ textAlign: "center", fontWeight: "500" }}
                            >
                              {new Date(season.end_date).toLocaleDateString(
                                "id-ID"
                              )}
                            </td>
                            <td>
                              {season.is_active ? (
                                <Badge bg="success" text="light">
                                  Active
                                </Badge>
                              ) : (
                                <Badge bg="secondary" text="light">
                                  Inactive
                                </Badge>
                              )}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <div
                                style={{
                                  display: "flex",
                                  gap: "4px",
                                  justifyContent: "center",
                                }}
                              >
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleEditSeason(season)}
                                  style={{
                                    padding: "4px 8px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <IconEdit size={14} />
                                  Edit
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeleteSeason(season)}
                                  style={{
                                    padding: "4px 8px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <IconTrash size={14} />
                                  Delete
                                </Button>
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  disabled={!season.is_active}
                                  onClick={() =>
                                    season.is_active &&
                                    handleForceEndSeason(season)
                                  }
                                  style={{
                                    padding: "4px 8px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <IconPlayerStop size={14} />
                                  Force End
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="5"
                            style={{ textAlign: "center", padding: "20px" }}
                          >
                            No season data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
                {seasons?.Pagination && (
                  <div style={{ padding: "16px" }}>{seasons.Pagination}</div>
                )}
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Tier CRUD Modal */}
      <Modal className="agent-rank-scope"
        show={showTierModal}
        onHide={handleTierModalClose}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedTier ? "Edit Tier" : "Create New Tier"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <TierForm
            initialData={selectedTier}
            onSuccess={handleTierFormSuccess}
            onError={handleTierFormError}
          />
        </Modal.Body>
      </Modal>

      {/* Season CRUD Modal */}
      <Modal className="agent-rank-scope"
        show={showSeasonModal}
        onHide={handleSeasonModalClose}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedSeason ? "Edit Season" : "Create New Season"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <SeasonForm
            initialData={selectedSeason}
            onSuccess={handleSeasonFormSuccess}
            onError={handleSeasonFormError}
          />
        </Modal.Body>
      </Modal>

      {/* Image View Modal */}
      
      <Modal className="agent-rank-scope"
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedImage?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ textAlign: "center" }}>
          {selectedImage && (
            <img
              src={selectedImage.url}
              alt={selectedImage.name}
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
              }}
            />
          )}
        </Modal.Body>
      </Modal>
      <PrintJson data={tiers} />
    </LayoutAgentRank>
  );
}
